import React, { useState, useEffect, useCallback, useRef } from 'react';
import { X, Check, Loader2, ChevronRight } from 'lucide-react';
import { Language, AIPersonalityKey } from '../types';
import {
  AI_PERSONALITIES,
  AIPersonalityProfile,
  getAllPersonalities,
  DEFAULT_PERSONALITY_KEY
} from '../shared/aiPersonalities';

// =====================================================
// PERSONALITY SELECTOR MODAL - Apple-grade UX v2
// =====================================================

interface PersonalitySelectorModalProps {
  isOpen: boolean;
  onClose: () => void;
  currentPersonalityKey: AIPersonalityKey;
  onSelect: (key: AIPersonalityKey) => Promise<void>;
  language: Language;
  isUpdating?: boolean;
}

type ModalState = 'idle' | 'saving' | 'success' | 'error';

export const PersonalitySelectorModal: React.FC<PersonalitySelectorModalProps> = ({
  isOpen,
  onClose,
  currentPersonalityKey,
  onSelect,
  language,
  isUpdating: externalUpdating = false
}) => {
  const [selectedKey, setSelectedKey] = useState<AIPersonalityKey>(currentPersonalityKey);
  const [isAnimating, setIsAnimating] = useState(false);
  const [modalState, setModalState] = useState<ModalState>('idle');
  const [errorMsg, setErrorMsg] = useState<string>('');
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Clean up timeout on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  // Reset state when modal opens
  useEffect(() => {
    if (isOpen) {
      setSelectedKey(currentPersonalityKey);
      setModalState('idle');
      document.body.style.overflow = 'hidden';
      requestAnimationFrame(() => {
        requestAnimationFrame(() => setIsAnimating(true));
      });
    } else {
      document.body.style.overflow = '';
      setIsAnimating(false);
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen, currentPersonalityKey]);

  const personalities = getAllPersonalities();
  const hasChanged = selectedKey !== currentPersonalityKey;
  const selectedPersonality = AI_PERSONALITIES[selectedKey];
  const isLoading = modalState === 'saving' || externalUpdating;

  // Handle card selection
  const handleCardSelect = useCallback((key: AIPersonalityKey) => {
    if (isLoading) return;
    setSelectedKey(key);
    setModalState('idle');
  }, [isLoading]);

  // Handle confirm - robust implementation with runtime validation
  const handleConfirm = useCallback(async () => {
    if (!hasChanged || isLoading) return;

    // Runtime validation - onSelect must be a function
    if (typeof onSelect !== 'function') {
      setErrorMsg('Handler not configured');
      setModalState('error');
      setTimeout(() => {
        setModalState('idle');
        setErrorMsg('');
      }, 3000);
      return;
    }

    setModalState('saving');
    const savingStartTime = Date.now();

    try {
      // Add timeout protection (10 seconds max)
      const timeoutPromise = new Promise<never>((_, reject) => {
        timeoutRef.current = setTimeout(() => {
          reject(new Error('Timeout'));
        }, 10000);
      });

      await Promise.race([
        onSelect(selectedKey),
        timeoutPromise
      ]);

      // Clear timeout on success
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }

      // Ensure minimum visibility of saving state (600ms)
      const elapsed = Date.now() - savingStartTime;
      const remainingTime = Math.max(0, 600 - elapsed);

      setTimeout(() => {
        // Show success state with enough time to be visible
        setModalState('success');
        setTimeout(() => {
          onClose();
        }, 1200); // Success state duration
      }, remainingTime);

    } catch (error: any) {
      const msg = error?.message || String(error) || 'Unknown error';
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
      setErrorMsg(msg);
      setModalState('error');
      // Reset to idle after showing error
      setTimeout(() => {
        setModalState('idle');
        setErrorMsg('');
      }, 5000);
    }
  }, [hasChanged, isLoading, onSelect, selectedKey, onClose]);

  // Handle close with animation
  const handleClose = useCallback(() => {
    if (isLoading) return;
    setIsAnimating(false);
    setTimeout(() => onClose(), 200);
  }, [isLoading, onClose]);

  const handleBackdropClick = useCallback((e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      handleClose();
    }
  }, [handleClose]);

  if (!isOpen) return null;

  // Translations
  const t = {
    title: {
      Italian: 'Scegli la tua Guida',
      English: 'Choose Your Guide',
      Spanish: 'Elige tu Guía',
      German: 'Wähle deinen Guide',
      French: 'Choisis ton Guide'
    },
    subtitle: {
      Italian: 'Ogni guida ha un approccio unico',
      English: 'Each guide has a unique approach',
      Spanish: 'Cada guía tiene un enfoque único',
      German: 'Jeder Guide hat einen einzigartigen Ansatz',
      French: 'Chaque guide a une approche unique'
    },
    confirm: {
      Italian: 'Conferma',
      English: 'Confirm',
      Spanish: 'Confirmar',
      German: 'Bestätigen',
      French: 'Confirmer'
    },
    saving: {
      Italian: 'Cambio in corso...',
      English: 'Changing...',
      Spanish: 'Cambiando...',
      German: 'Wird geändert...',
      French: 'Changement...'
    },
    success: {
      Italian: 'Fatto!',
      English: 'Done!',
      Spanish: '¡Listo!',
      German: 'Fertig!',
      French: 'Terminé!'
    },
    selectDifferent: {
      Italian: 'Seleziona una guida diversa',
      English: 'Select a different guide',
      Spanish: 'Selecciona otra guía',
      German: 'Wähle einen anderen Guide',
      French: 'Sélectionne un autre guide'
    },
    current: {
      Italian: 'Attuale',
      English: 'Current',
      Spanish: 'Actual',
      German: 'Aktuell',
      French: 'Actuel'
    },
    error: {
      Italian: 'Errore - Tocca per riprovare',
      English: 'Error - Tap to retry',
      Spanish: 'Error - Toca para reintentar',
      German: 'Fehler - Tippen zum Wiederholen',
      French: 'Erreur - Appuie pour réessayer'
    }
  };

  const getText = (key: keyof typeof t) => t[key][language] || t[key].English;

  // Button content based on state
  const getButtonContent = () => {
    if (modalState === 'saving') {
      return (
        <div className="flex items-center gap-2.5">
          <Loader2 size={22} className="animate-spin" strokeWidth={2.5} />
          <span className="font-medium">{getText('saving')}</span>
        </div>
      );
    }
    if (modalState === 'success') {
      return (
        <>
          <Check size={20} strokeWidth={3} />
          <span>{getText('success')}</span>
        </>
      );
    }
    if (modalState === 'error') {
      return <span className="text-red-500 text-xs px-2 truncate">{errorMsg || getText('error')}</span>;
    }
    if (hasChanged) {
      return (
        <>
          <span className="text-xl">{selectedPersonality.emoji}</span>
          <span>{getText('confirm')}</span>
          <ChevronRight size={18} className="opacity-60" />
        </>
      );
    }
    return <span className="text-[15px]">{getText('selectDifferent')}</span>;
  };

  // Button style based on state
  const getButtonStyle = () => {
    if (modalState === 'success') {
      return 'bg-green-500 text-white transition-all duration-300';
    }
    if (modalState === 'error') {
      return 'bg-red-50 text-red-600 border-2 border-red-200 transition-all duration-300';
    }
    if (modalState === 'saving') {
      // Loading state - show gradient without pulse to avoid flickering
      return `bg-gradient-to-b ${selectedPersonality.color} text-white/90 shadow-lg shadow-black/15 transition-all duration-300`;
    }
    if (hasChanged && modalState !== 'saving') {
      return `bg-gradient-to-b ${selectedPersonality.color} text-white shadow-lg shadow-black/15 active:scale-[0.98] transition-all duration-200`;
    }
    return 'bg-black/[0.04] text-black/30 transition-all duration-200';
  };

  return (
    <div
      className={`fixed inset-0 z-[10000] flex items-end sm:items-center justify-center transition-all duration-300 ease-out ${
        isAnimating ? 'bg-black/40 backdrop-blur-sm' : 'bg-transparent pointer-events-none'
      }`}
      onClick={handleBackdropClick}
    >
      {/* Modal Container */}
      <div
        className={`w-full sm:max-w-[420px] bg-white sm:rounded-[28px] rounded-t-[28px] shadow-2xl flex flex-col transform transition-all duration-300 ease-[cubic-bezier(0.32,0.72,0,1)] overflow-hidden ${
          isAnimating
            ? 'translate-y-0 opacity-100 scale-100'
            : 'translate-y-full sm:translate-y-4 sm:scale-95 opacity-0'
        }`}
        style={{
          maxHeight: 'min(90vh, 90dvh)',
          paddingBottom: 'env(safe-area-inset-bottom, 0px)'
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Drag Handle - Mobile only */}
        <div className="sm:hidden flex justify-center pt-2.5 pb-1">
          <div className="w-9 h-1 bg-black/10 rounded-full" />
        </div>

        {/* Header */}
        <div className="shrink-0 px-5 pt-1 sm:pt-5 pb-3">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <h2 className="text-xl font-semibold text-black tracking-tight">
                {getText('title')}
              </h2>
              <p className="text-sm text-black/45 mt-0.5">
                {getText('subtitle')}
              </p>
            </div>
            <button
              onClick={handleClose}
              disabled={isLoading}
              className="w-8 h-8 rounded-full bg-black/5 hover:bg-black/10 flex items-center justify-center transition-all duration-150 active:scale-90 disabled:opacity-30"
            >
              <X size={18} className="text-black/50" strokeWidth={2} />
            </button>
          </div>
        </div>

        {/* Personality Grid */}
        <div className="flex-1 overflow-y-auto overscroll-contain px-3 pb-3 min-h-0">
          <div className="grid grid-cols-2 gap-2.5">
            {personalities.map((personality) => {
              const isSelected = selectedKey === personality.key;
              const isCurrent = currentPersonalityKey === personality.key;

              return (
                <PersonalityCard
                  key={personality.key}
                  personality={personality}
                  language={language}
                  isSelected={isSelected}
                  isCurrent={isCurrent}
                  onSelect={() => handleCardSelect(personality.key)}
                  disabled={isLoading}
                  currentLabel={getText('current')}
                />
              );
            })}
          </div>
        </div>

        {/* Footer */}
        <div className="shrink-0 p-4 pt-3 bg-gradient-to-t from-white via-white to-white/80">
          <button
            onClick={modalState === 'error' ? handleConfirm : handleConfirm}
            disabled={(!hasChanged && modalState === 'idle') || modalState === 'saving' || modalState === 'success'}
            className={`w-full h-[52px] rounded-2xl font-semibold text-[16px] transition-all duration-200 flex items-center justify-center gap-2 ${getButtonStyle()}`}
          >
            {getButtonContent()}
          </button>
        </div>
      </div>
    </div>
  );
};

// =====================================================
// PERSONALITY CARD - Refined Apple-style design
// =====================================================

interface PersonalityCardProps {
  personality: AIPersonalityProfile;
  language: Language;
  isSelected: boolean;
  isCurrent: boolean;
  onSelect: () => void;
  disabled?: boolean;
  currentLabel: string;
}

const PersonalityCard: React.FC<PersonalityCardProps> = ({
  personality,
  language,
  isSelected,
  isCurrent,
  onSelect,
  disabled = false,
  currentLabel
}) => {
  const name = personality.names[language] || personality.names.English;
  const shortDesc = personality.shortDescriptions[language] || personality.shortDescriptions.English;

  return (
    <button
      onClick={onSelect}
      disabled={disabled}
      className={`relative flex flex-col items-center p-3.5 rounded-2xl transition-all duration-200 ease-out active:scale-[0.97] disabled:opacity-40 disabled:pointer-events-none min-h-[130px] ${
        isSelected
          ? `bg-gradient-to-b ${personality.color} shadow-lg`
          : isCurrent
            ? 'bg-white border-2 border-black/10 shadow-sm'
            : 'bg-white border border-black/[0.06] hover:border-black/10 hover:shadow-md'
      }`}
    >
      {/* Current badge - centered at top */}
      {isCurrent && !isSelected && (
        <div className="absolute -top-2.5 left-1/2 -translate-x-1/2 px-2.5 py-0.5 bg-black text-white text-[9px] font-bold rounded-full uppercase tracking-wider">
          {currentLabel}
        </div>
      )}

      {/* Selected checkmark */}
      {isSelected && (
        <div className="absolute -top-1.5 -right-1.5 w-6 h-6 bg-white rounded-full flex items-center justify-center shadow-lg border-2 border-white">
          <Check size={14} className="text-green-500" strokeWidth={3} />
        </div>
      )}

      {/* Emoji */}
      <div className={`text-[32px] mb-1.5 transition-transform duration-200 ${
        isSelected ? 'scale-105' : ''
      }`}>
        {personality.emoji}
      </div>

      {/* Name */}
      <h3 className={`text-[12px] font-semibold text-center leading-tight mb-1 ${
        isSelected ? 'text-white' : 'text-black/90'
      }`}>
        {name}
      </h3>

      {/* Description */}
      <p className={`text-[10px] text-center leading-snug line-clamp-2 ${
        isSelected ? 'text-white/75' : 'text-black/45'
      }`}>
        {shortDesc}
      </p>
    </button>
  );
};

// =====================================================
// HEADER PERSONALITY BUTTON
// =====================================================

interface PersonalityHeaderButtonProps {
  personalityKey: AIPersonalityKey;
  language: Language;
  onClick: () => void;
  compact?: boolean;
}

export const PersonalityHeaderButton: React.FC<PersonalityHeaderButtonProps> = ({
  personalityKey,
  language,
  onClick,
}) => {
  const personality = AI_PERSONALITIES[personalityKey] || AI_PERSONALITIES[DEFAULT_PERSONALITY_KEY];
  const name = personality.names[language] || personality.names.English;

  const tooltips: Record<Language, string> = {
    Italian: 'Cambia guida AI',
    English: 'Change AI guide',
    Spanish: 'Cambiar guía IA',
    German: 'KI-Guide ändern',
    French: 'Changer guide IA'
  };

  return (
    <button
      onClick={onClick}
      className={`group flex items-center justify-center w-10 h-10 bg-gradient-to-b ${personality.color} rounded-xl text-white shadow-md hover:shadow-lg hover:scale-105 transition-all duration-200 active:scale-95`}
      title={`${tooltips[language] || tooltips.English}: ${name}`}
    >
      <span className="text-xl group-hover:scale-110 transition-transform duration-200">
        {personality.emoji}
      </span>
    </button>
  );
};

// =====================================================
// PERSONALITY PREVIEW (for settings)
// =====================================================

interface PersonalityPreviewProps {
  personalityKey: AIPersonalityKey;
  language: Language;
  onChangeClick?: () => void;
  showDescription?: boolean;
}

export const PersonalityPreview: React.FC<PersonalityPreviewProps> = ({
  personalityKey,
  language,
  onChangeClick,
  showDescription = true
}) => {
  const personality = AI_PERSONALITIES[personalityKey] || AI_PERSONALITIES[DEFAULT_PERSONALITY_KEY];
  const name = personality.names[language] || personality.names.English;
  const description = personality.descriptions[language] || personality.descriptions.English;

  const changeText: Record<Language, string> = {
    Italian: 'Cambia',
    English: 'Change',
    Spanish: 'Cambiar',
    German: 'Ändern',
    French: 'Changer'
  };

  return (
    <div className="p-4 rounded-2xl bg-white border border-black/[0.06]">
      <div className="flex items-start gap-3">
        <div className={`w-11 h-11 rounded-xl bg-gradient-to-b ${personality.color} flex items-center justify-center shadow-md flex-shrink-0`}>
          <span className="text-xl">{personality.emoji}</span>
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="font-semibold text-black text-[15px]">{name}</h3>
          {showDescription && (
            <p className="text-[13px] text-black/50 mt-0.5 line-clamp-2">{description}</p>
          )}
        </div>
        {onChangeClick && (
          <button
            onClick={onChangeClick}
            className="px-3.5 py-1.5 text-[13px] font-medium text-black bg-black/5 rounded-full hover:bg-black/10 transition-colors flex-shrink-0 active:scale-95"
          >
            {changeText[language] || changeText.English}
          </button>
        )}
      </div>
    </div>
  );
};

export default PersonalitySelectorModal;
